/**
 * Copyright (c) 2010-2022 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoggerService } from '../../../../../modules/shared/services/logger.service';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import {
    CheckResetKey,
    ResetForgottenPassword,
    ResetForgottenPasswordFailure,
    ResetForgottenPasswordSuccess,
} from '../../../state/auth.actions';
import { filter, map, switchMap, switchMapTo, take } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Navigate } from '@ngxs/router-plugin';
import { FlexFillDirective, LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatToolbar } from '@angular/material/toolbar';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ResetPasswordFormComponent } from '../forms/reset-password-form/reset-password-form.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'dash-gis-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    imports: [
        FlexFillDirective,
        LayoutDirective,
        LayoutAlignDirective,
        MatCard,
        MatToolbar,
        MatCardContent,
        CdkTrapFocus,
        MatProgressSpinner,
        ResetPasswordFormComponent,
        AsyncPipe,
    ],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    resetKey$: Observable<string>;
    valid$: Observable<boolean>;
    beenChecked$: Observable<boolean>;

    private dispose$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private store: Store,
        private actions$: Actions,
        private route: ActivatedRoute,
        private snack: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.resetKey$ = this.route.queryParamMap.pipe(map((paramMap) => paramMap.get('key')));
        this.valid$ = this.route.queryParamMap.pipe(map((paramMap) => paramMap.get('valid')?.toLowerCase() === 'true'));
        this.beenChecked$ = this.route.queryParamMap.pipe(map((paramMap) => paramMap.has('valid')));

        this.beenChecked$
            .pipe(
                filter((c) => !c),
                take(1),
                switchMap(() => this.resetKey$.pipe(take(1)))
            )
            .subscribe((resetKey) => this.store.dispatch(new CheckResetKey(resetKey)));

        const successMessage = 'Your password has been successfully changed. You will now be logged in.';
        this.actions$
            .pipe(ofActionSuccessful(ResetForgottenPasswordSuccess))
            .subscribe(() => this.snack.open(successMessage, 'Got it'));

        const failureMessage = 'There was an error resetting your password:<br />';
        this.actions$
            .pipe(ofActionSuccessful(ResetForgottenPasswordFailure))
            .subscribe((action) => this.snack.open(failureMessage + action.message, 'OK'));
    }

    ngOnDestroy(): void {
        this.dispose$.next();
    }

    resetPassword(event) {
        this.valid$
            .pipe(
                filter(Boolean),
                take(1),
                switchMap(() => this.resetKey$.pipe(take(1)))
            )
            .subscribe((resetKey) => {
                this.store.dispatch(
                    new ResetForgottenPassword({
                        newPassword: event.newPassword,
                        newPasswordConfirm: event.newPasswordConfirm,
                        resetKey: resetKey,
                    })
                );
            });
    }

    cancelReset(): void {
        this.store.dispatch(
            new Navigate(
                ['/'],
                {
                    key: null,
                    valid: null,
                },
                { queryParamsHandling: 'merge' }
            )
        );
    }
}
